/**
 *
 * Super basic Symbol implementation that extends PIXI.Sprite
 *
 */

import {DeadwoodRip} from "../DeadwoodRip";
import {DeadwoodRipSymbolState} from "./DeadwoodRipSymbolConfig";
import {IParsedStackedSymbolName, StackedSymbol} from "../../core/reel/stackedsymbol/StackedSymbol";
import {IBaseSymbol} from "../../core/symbol/BaseSymbol";
import {MultiSprites} from "@nolimitcity/slot-game/bin/animation/MultiSprites";
import {gsap, TimelineLite} from "gsap";
import {ShootableItem} from "../view/shooting/ShootableItem";
import {DeadwoodRipSound} from "../sound/DeadwoodRipSound";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {DeadwoodRipGameAssets} from "../DeadwoodRipGameAssets";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {DeadwoodRipLayers, Layers} from "../view/DeadwoodRipLayers";

export class DeadwoodRipSymbol extends ShootableItem implements IBaseSymbol {
    public static DEFAULT_WIDTH:number = 144;
    public static DEFAULT_HEIGHT:number = 144;

    public symbolWidth:number = 144;
    public symbolHeight:number = 144;
    public bottomPadding:number = 0;
    public removed:boolean = false;

    protected _ripAssetName:string = "xRip";
    protected _noWinTint:number = 0x6F6666;
    protected _xRipTint:number = 0x999999;

    public splitSplashDelay:number = 0.15;
    protected _splitSymbolAnimationDuration:number = 0.1;

    public symbolSize:number = 1;
    public symbolWays:number = 1;
    public symbolName:string;
    public stackedSymbolName:IParsedStackedSymbolName;
    public reelId:number;

    protected _parts:MultiSprites[];
    protected _centerPosition:PIXI.Point;
    protected _deadContainer:PIXI.Container;
    protected _deadCharacter:PIXI.spine.Spine;
    protected _xRipPatch:PIXI.Sprite;
    protected _splitContainer:PIXI.Container;
    protected _multiplierContainer:PIXI.Container;
    protected _multiplierText:PIXI.BitmapText;
    protected _partPatches:PIXI.Container[] = [];
    protected _partPatchContainer:PIXI.Container;

    protected _currentMultiplier:number = 0;
    public get currentMultiplier() {
        return this._currentMultiplier;
    }

    public get symbolStep():number {
        return this.symbolHeight + this.bottomPadding;
    }

    public get actualSymbolHeight():number {
        return this.symbolHeight;
    }

    constructor(symbolName:string, reelId:number, initState:DeadwoodRipSymbolState) {
        super({
            name : symbolName,
            target : new PIXI.Sprite(),
            holeScale : 1,
            view0 : new PIXI.Container(),
            view1 : new PIXI.Container(),
            soundPlayer : (stereoIndex:number) => this.playGunShotSound(stereoIndex),
            holeGetter : () => ShootableItem.getDefaultWoodHole(0.6),
            sparkGetter : () => ShootableItem.getDefaultWoodSpark(1),
            killabled : true
        });
        this.symbolName = symbolName;
        this.stackedSymbolName = StackedSymbol.parseStackedSymbolName(symbolName, reelId);
        this.reelId = reelId;
        this.initAnimation();
        this.getStateAnimation(initState, {duration : 0, alpha : 1}, {duration : 0});
    }

    protected playGunShotSound(stereoIndex:number):TimelineLite {
        return DeadwoodRipSound.getDefaultGunShotWoodTimeline(stereoIndex);
    }

    protected initAnimation():void {
        this._centerPosition = this.getCenterPosition();
        this._parts = [];
        const part = new MultiSprites();
        part.position.set(this._centerPosition.x, this._centerPosition.y);
        this.addChild(part);
        this._parts.push(part);

        this._multiplierContainer = new PIXI.Container();
        this._multiplierContainer.scale.set(1);
        this._multiplierContainer.position.set(this._centerPosition.x + this.symbolWidth * 0.5 - 10, this._centerPosition.y);
        this._multiplierText = this.getMultiplierText();
        this._multiplierContainer.addChild(this._multiplierText);
    }

    protected getMultiplierText():PIXI.BitmapText {
        const text = new PIXI.BitmapText("", {
            fontName : DeadwoodRipGameAssets.BITMAP_FONT_WAYS,
            fontSize : 24,
            tint : 0xffc525
        });
        (<PIXI.Point>text.anchor).set(1, 1.3);
        text.letterSpacing = -30;
        return text;
    }

    protected getCenterPosition():PIXI.Point {
        return new PIXI.Point(0, 0);
    }

    public getSymbolShotRect():PIXI.Rectangle {
        return new PIXI.Rectangle(-50, -50, 100, 100);
    }

    public getStateAnimation(state:DeadwoodRipSymbolState, inVars:gsap.TweenVars, outVars:gsap.TweenVars, frames?:number[]):TimelineLite {
        this.createState(state);
        const tl = new TimelineLite();
        this._parts.forEach(part => {
            tl.add(part.playSprite(state, inVars, outVars, frames), 0);
        });

        if(state == DeadwoodRipSymbolState.NO_WIN || state == DeadwoodRipSymbolState.XRIP) {
            tl.add(() => {
                if(this._deadCharacter) {
                    const entry = this._deadCharacter.state.getCurrent(1);
                    if(entry) {
                        entry.trackTime = entry.animationEnd;
                    }
                }
                this.getStateColorChangeAnimation(state)
            }, 0);
        }

        return tl;
    }

    protected getStateColorChangeAnimation(stateName?:string):void {
        // No need to tint wildPath since it can only be no win state in reel 5 inside the reel frame, not inside symbol.
        if(!stateName) {
            stateName = this._parts[0].currentSpriteName;
        }
        const tint = stateName === DeadwoodRipSymbolState.NO_WIN ? this._noWinTint : (stateName === DeadwoodRipSymbolState.XRIP ? this._xRipTint : 0xffffff);

        if(this._shootContainer) {
            this._shootContainer.children.forEach(c => {
                if(c instanceof PIXI.Sprite) {
                    c.tint = tint;
                }
            });
        }

        if(this._deadCharacter) {
            this._deadCharacter.tint = tint;
        }

        if(this._multiplierText) {
            this._multiplierText.tint = tint;
        }

        if(this._splitContainer) {
            this._splitContainer.children.forEach(c => {
                if(c instanceof PIXI.Sprite) {
                    c.tint = tint;
                }
            });
        }
    }

    protected createState(state:DeadwoodRipSymbolState):void {
        let updateLayers:boolean = false;
        this._parts.forEach(part => {
            if(!part.sprites[state]) {
                part.addSprites({
                    name : state,
                    textures : this.getStateTexture(state),
                    anchor : new PIXI.Point(0.5, 0.5)
                });
                if(state === DeadwoodRipSymbolState.NO_WIN) {
                    part.sprites[state].tint = this._noWinTint;
                }
                if(state === DeadwoodRipSymbolState.XRIP) {
                    part.sprites[state].tint = this._xRipTint;
                }
                updateLayers = true;
            }
        })
        if(updateLayers) {
            this.updateLayers();
        }
    }

    protected updateLayers():void {
        if(this._deadContainer) {
            this.addChild(this._deadContainer);
        }
        if(this._partPatchContainer) {
            this.addChild(this._partPatchContainer);
        }
        if(this._splitContainer) {
            this.addChild(this._splitContainer);
        }
        if(this._shootContainer) {
            this.addChild(this._shootContainer);
        }
        if(this._multiplierContainer) {
            this.addChild(this._multiplierContainer);
        }
        if(this._xRipPatch) {
            this.addChild(this._xRipPatch);
        }
    }

    protected getStateTexture(state?:DeadwoodRipSymbolState):PIXI.Texture[] {
        let texturePath = `${this.symbolName}/${this.symbolName}`;
        if(this.symbolSize > 1) {
            texturePath += `_S${this.symbolSize}`;
        }
        if(state === DeadwoodRipSymbolState.SPIN) {
            texturePath += `_${state}`;
        }
        return DeadwoodRip.assetLoader.getTextures(texturePath);
    }

    public onRemove():void {
        if(this._deadCharacter) {
            this._deadCharacter.state.setEmptyAnimations(0);
            if(this._deadCharacter.parent) {
                this._deadCharacter.parent.removeChild(this._deadCharacter);
            }
            this._deadCharacter.destroy();
        }

        super.onRemove();
        this.destroy({children : true});
        this.removed = true;
    }

    public getGlobalCenterPosition():PIXI.Point {
        if(this.parent) {
            return new PIXI.Point(this.position.x + this._centerPosition.x + this.parent.x,
                this.position.y + this._centerPosition.y + this.parent.y);
        }
        return new PIXI.Point(this.position.x + this._centerPosition.x,
            this.position.y + this._centerPosition.y);
    }

    public shotAtRandomPosition(isFeatureShoot:boolean = false, alwaysReset:boolean = false, animate:boolean = true,
                                splashGetter?:() => PIXI.Sprite, holeGetter?:() => PIXI.Sprite, sparkGetter?:() => TimelineSprite):TimelineLite {
        const rect = this.getSymbolShotRect();
        const position = new PIXI.Point(MathHelper.randomIntInRange(rect.x, rect.width + rect.x), MathHelper.randomIntInRange(rect.y, rect.height + rect.y));
        return this.shotAt([position], isFeatureShoot, alwaysReset, animate, splashGetter, holeGetter, sparkGetter);
    }

    // // TODO: spark should not be tint.
    // public shotAt(positions:PIXI.Point[], isFeatureShoot:boolean = false, alwaysReset:boolean = false, animate:boolean = true,
    //               splashGetter?:() => PIXI.Sprite, holeGetter?:() => PIXI.Sprite, sparkGetter?:() => TimelineSprite):TimelineLite {
    //     return super.shotAt(positions, isFeatureShoot, alwaysReset, animate, splashGetter, holeGetter, sparkGetter);
    // }

    public playOnShot():TimelineLite {
        this.createDeadContainer();
        const tl = super.playOnShot();
        const aniName = this.getOnShotAnimationName();
        tl.add(() => {
            this._deadCharacter.visible = true;
            this._deadCharacter.state.setAnimation(0, aniName, false);
        });
        tl.add(() => {}, `+=${this._deadCharacter.spineData.findAnimation(aniName).duration}`);
        return tl;
    }

    protected getOnShotAnimationName():string {
        return `1xL_Shoot`;
    }

    protected playKill():TimelineLite {
        this.createDeadContainer();
        const tl = new TimelineLite();
        const aniName = this.getKillAnimationName();
        tl.add(() => {
            this._deadCharacter.visible = true;
            this._deadCharacter.state.setAnimation(1, aniName, false);
            this.playKillSound();
        });
        tl.add(() => {
            if(this.removed) {
                return;
            }
            if(this._parts[0].currentSpriteName === DeadwoodRipSymbolState.NORMAL) {
                const loopAniName = this.getDeadLoopAnimationName();
                if(loopAniName.length > 0) {
                    this._deadCharacter.state.setAnimation(1, loopAniName, true);
                }
            } else {
                this.getStateColorChangeAnimation();
            }
        }, `+=${this._deadCharacter.spineData.findAnimation(aniName).duration}`);
        this._parts.forEach(p => {
            tl.add(gsap.to(p, {alpha : 0, duration : 0.2}), 0);
        });
        return tl;
    }

    protected playKillSound():void {}

    protected getKillAnimationName():string {
        return `${this.stackedSymbolName.symName}Dead`;
    }

    protected getDeadLoopAnimationName():string {
        return ``;
    }

    public playIndividualKill():TimelineLite {
        this.createDeadContainer();
        const tl = new TimelineLite();
        const aniName = this.getIndividualAnimationName();
        tl.add(() => {
            if(!this.removed) {
                this._deadCharacter.visible = true;
                this._deadCharacter.state.setAnimation(1, aniName, false);
            }
        });
        tl.add(() => {
            if(!this.removed) {
                const loopAniName = this.getDeadLoopAnimationName();
                if(loopAniName.length > 0) {
                    this._deadCharacter.state.setAnimation(1, loopAniName, true);
                }
            }
        }, `+=${this._deadCharacter.spineData.findAnimation(aniName).duration}`);
        this._parts.forEach(p => {
            tl.add(gsap.to(p, {alpha : 0, duration : 0.2}), 0);
        });
        return tl;
    }

    protected getIndividualAnimationName():string {
        return `${this.stackedSymbolName.symName}Toggle`;
    }

    protected createDeadContainer():void {
        if(!this._deadContainer) {
            this._deadContainer = new PIXI.Container();
            this._deadContainer.position.set(this._centerPosition.x, this._centerPosition.y);
            this._deadCharacter = new PIXI.spine.Spine(DeadwoodRip.assetLoader.getSpineAsset(this.getCharacterAssetName()));
            this._deadCharacter.autoUpdate = true;
            this._deadContainer.addChild(this._deadCharacter);
            this._deadCharacter.visible = false;

            this.updateLayers();
        }
    }

    protected getCharacterAssetName():string {
        return "initialWin";
    }

    public playXRip():TimelineLite {
        if(!this._xRipPatch) {
            this._xRipPatch = new PIXI.Sprite(DeadwoodRip.assetLoader.getTextures(this._ripAssetName)[0]);
            this._xRipPatch.anchor.set(0.5);
            this._xRipPatch.position.set(this._centerPosition.x, this._centerPosition.y);
            this.updateLayers();
        }

        const tl = new TimelineLite();
        tl.add(() => {
            this._xRipPatch.visible = true;
        });
        tl.add(this.getStateAnimation(DeadwoodRipSymbolState.XRIP,
            {duration : 0.5, alpha : 1},
            {duration : 0.5, alpha : 0}));
        const xRipTl = gsap.timeline();
        xRipTl.to(this._xRipPatch.scale, {x : 1.1, y : 1.1, duration : 0.3})
            .to(this._xRipPatch.scale, {x : 1, y : 1, duration : 0.4});
        tl.add(xRipTl, 0);

        return tl;
    }

    // type = 0 reel split, type = 1 xSplit
    public split(type:number, noWin:boolean):TimelineLite {
        const tl = new TimelineLite();
        const oldSize = this.symbolSize;
        this.symbolSize *= 2;
        this.addSplitSymbolAnimation(tl, oldSize, type, noWin);
        this.updateLayers();
        return tl;
    }

    protected addSplitSymbolAnimation(tl:TimelineLite, oldSymbolSize:number, type:number, noWin:boolean):void {
        const oldParts:PIXI.Container[] = this._partPatches.concat(this._parts);
        this._partPatches = [];
        this._parts = [];
        if(!this._splitContainer) {
            this._splitContainer = new PIXI.Container();
        }
        for(let i = 0; i < this.symbolSize; i++) {
            const part = new MultiSprites();
            part.y = this._centerPosition.y - this.actualSymbolHeight * 0.5 + (i + 0.5) * this.actualSymbolHeight / this.symbolSize;
            part.alpha = 0;
            this.addChild(part);
            this._parts.push(part);
            tl.add(() => {
                this.addSplitPartOverlay(part);
            }, noWin ? 0.01 : this.splitSplashDelay);
            if(i > 0 && i % 2 != 0) {
                const cut = new PIXI.Sprite(DeadwoodRip.assetLoader.getTextures("splitdivider")[0]);
                cut.anchor.set(0.5);
                cut.position.set(this._centerPosition.x, part.y - 0.5 * this.actualSymbolHeight / this.symbolSize);
                tl.add(() => {
                    this._splitContainer.addChild(cut);
                }, noWin ? 0.01 : this.splitSplashDelay);
                if(!noWin) {
                    const splitSpine = new PIXI.spine.Spine(DeadwoodRip.assetLoader.getSpineAsset("split"));
                    splitSpine.autoUpdate = true;
                    const globalPosition = this.getGlobalCenterPosition();
                    splitSpine.position.set(globalPosition.x + cut.position.x, globalPosition.y + cut.position.y);
                    tl.add(() => {
                        DeadwoodRipLayers.getLayer(Layers.WIN_PRESENTATIONS)!.addChild(splitSpine);
                        splitSpine.state.setAnimation(0, "animation", false);
                    }, 0);
                    tl.add(() => {
                        splitSpine.parent.removeChild(splitSpine);
                        splitSpine.destroy();
                    }, splitSpine.spineData.findAnimation("animation").duration);
                }
            }
        }
        tl.add(this.getStateAnimation(DeadwoodRipSymbolState.NORMAL,
            {duration : 0, alpha : 1},
            {duration : 0, alpha : 0}), 0);
        oldParts.forEach(part => {
            tl.add(() => {
                part.parent.removeChild(part);
                part.destroy();
            }, noWin ? 0.01 : this.splitSplashDelay);
        });

        this._parts.forEach((part, index) => {
            if(type === 0) { // axe
                tl.add([
                    () => {
                        part.y = index === 0 ? -30 : -15;
                        part.alpha = index === 0 ? 1 : 0;
                    },
                    gsap.to(part, {
                        duration : noWin ? 0 : this._splitSymbolAnimationDuration,
                        alpha : 1,
                        y : part.y
                    })
                ], noWin ? 0.01 : this.splitSplashDelay);
            } else {
                const partTl = new TimelineLite();
                if(!noWin) {
                    partTl.add(gsap.to(part, {x : 3, duration : 0.033}));
                    partTl.add(gsap.to(part, {x : 5, duration : 0.433}));
                }
                partTl.add(gsap.to(part, {x : 0, duration : noWin ? 0.01 : 0.1}));
                tl.add([
                    () => {part.alpha = 1},
                ], noWin ? 0.01 : this.splitSplashDelay);
            }
        });
        this.updateLayers();
    }

    protected addSplitPartOverlay(part:MultiSprites):void {}

    public cylinderSplit(reelId:number, symbolId:number):TimelineLite {
        const tl = new TimelineLite();
        tl.add(() => {
            DeadwoodRip.ways.updateSymbolWays(reelId, symbolId, DeadwoodRip.ways.getSymbolValue(reelId, symbolId) * 2, true);
            this.symbolWays *= 2;
            this._multiplierText.text = `x${this.symbolWays}`;
            this._multiplierText.scale.set(1);
            this._multiplierText.updateTransform();
            if(this._multiplierText.textWidth > 100) {
                this._multiplierText.scale.x = MathHelper.roundToDecimals(100 / this._multiplierText.textWidth, 2);
            }
            this.updateLayers();
        });
        tl.add([
            gsap.to(this._multiplierContainer.scale, {x : 1.5, y : 1.3, duration : 0.1}),
            // gsap.to(this._multiplierGlow, {alpha : 1, duration : 0.1})
        ]);
        tl.add([
            gsap.to(this._multiplierContainer.scale, {x : 1, y : 1, duration : 0.5}),
            // gsap.to(this._multiplierGlow, {alpha : 0, duration : 0.5})
        ]);

        return tl;
    }
}